import { useEffect, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";

const POLL_MS = 60000;

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.max(1, Math.floor((Date.now() - then) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

export default function NotificationsBell() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch {
      // silent — bell just stays empty
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [user, load]);

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setItems((prev) => prev.map((n) => ({ ...n, read_at: n.read_at || new Date().toISOString() })));
      setUnread(0);
    } catch { /* noop */ }
  };

  if (!user) return null;

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button
          data-testid="notifications-bell"
          aria-label="Notifications"
          className="relative rounded-full p-2 hover:bg-black/5 transition-colors"
        >
          <Bell className="h-5 w-5" style={{ color: "var(--ink)" }} />
          {unread > 0 && (
            <span
              data-testid="notifications-badge"
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-semibold flex items-center justify-center text-white"
              style={{ backgroundColor: "var(--terracotta)" }}
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-96 p-0 border rounded-sm shadow-xl"
        style={{ backgroundColor: "#fff", borderColor: "#ece4d6" }}
        data-testid="notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "#ece4d6" }}>
          <div className="font-serif text-lg" style={{ color: "var(--ink)" }}>Notifications</div>
          {unread > 0 && (
            <button
              data-testid="notifications-mark-all-read"
              onClick={markAllRead}
              className="text-xs uppercase tracking-widest hover:opacity-70"
              style={{ color: "var(--terracotta)" }}
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto" data-testid="notifications-list">
          {items.length === 0 ? (
            <div className="p-8 text-center" data-testid="notifications-empty">
              <div className="font-serif text-lg" style={{ color: "var(--ink)" }}>All caught up.</div>
              <div className="mt-1 text-xs" style={{ color: "var(--ink-soft)" }}>
                Approvals, rejections, and event updates will land here.
              </div>
            </div>
          ) : (
            items.map((n) => (
              <div
                key={n.id}
                data-testid={`notification-${n.id}`}
                className="px-4 py-3 border-b last:border-b-0 hover:bg-[color:var(--bone)] transition-colors"
                style={{ borderColor: "#ece4d6" }}
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-1.5 block w-2 h-2 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: n.read_at
                        ? "transparent"
                        : n.type === "application_rejected"
                        ? "var(--ink-soft)"
                        : "var(--terracotta)",
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium leading-snug" style={{ color: "var(--ink)" }}>{n.title}</div>
                    <div className="mt-1 text-xs leading-relaxed" style={{ color: "var(--ink-soft)" }}>{n.body}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
